import React, { FC, useEffect, useMemo, useState } from 'react';
import { observer, Provider } from 'mobx-react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { Route, Router, Switch } from 'react-router-dom';
import Loader from 'components/common/Loader';
import Snackbar from 'components/common/Snackbar';
import StoreModel from 'models/Index';
import LoaderModel from 'models/shared/LoaderModel';
import ConfigurationModel from 'models/shared/ConfigurationModel';
import {
    i18nInit,
    Services,
    RoutePaths,
    logger,
    LogEvents,
    history,
} from 'helpers';
import { AdminFlow, CommonFlow } from 'pages';
import { setupTheme } from 'helpers/theme';
import { defaultThemeSettings } from 'helpers/constants/defaultThemeSettings';
import { CashCollectionModalPromt } from 'containers/modals';

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        primary: true;
        secondary: true;
    }
}

// use this link to create theme: https://material.io/resources/color

const Store: StoreModel = new StoreModel(
    new LoaderModel(),
    new ConfigurationModel()
);

const App: FC = () => {
    const deviceProfile = Store.deviceProfile;
    const [initialized, setInitialized] = useState<boolean>(false);
    const theme = useMemo(
        () => setupTheme(deviceProfile?.theme ?? defaultThemeSettings),
        [deviceProfile?.theme]
    );

    const services = {
        [Services.STORE]: Store,
        [Services.SNACKBAR]: Store.snackbar,
        [Services.BILLPAY]: Store.billpay,
        [Services.PAYMENT_WIZARD]: Store.wizard,
    };

    const handleError = (event: any) => {
        const { message, error } = event;
        logger.logError(
            'Unhandled client script exception',
            LogEvents.UnhandledScriptError,
            { message, error }
        );
        return true;
    };

    useEffect(() => {
        window.addEventListener('error', handleError);

        Store.loader.show();
        Store.init()
            .then((_) => i18nInit(Store.language))
            .finally(() => {
                setInitialized(true);
                Store.loader.hide();
            });
    }, []);

    useEffect(() => {
        const unlisten = history.listen((location) => {
            Store.tryUpdateProfileConfig(location.pathname);
        });
        return unlisten;
    }, []);

    return initialized ? (
        <div className='App'>
            <StyledEngineProvider injectFirst>
                <ThemeProvider theme={theme}>
                    <Provider {...services}>
                        <CssBaseline />
                        <Router history={history}>
                            <Switch>
                                <Route
                                    path={RoutePaths.AdminHome}
                                    component={AdminFlow}
                                />
                                <Route
                                    path={RoutePaths.IdleScreen}
                                    component={CommonFlow}
                                />
                            </Switch>
                        </Router>

                        <Loader loader={Store.loader} />
                        <Snackbar snackbar={Store.snackbar} />
                        <CashCollectionModalPromt store={Store} />
                    </Provider>
                </ThemeProvider>
            </StyledEngineProvider>
        </div>
    ) : (
        <Loader loader={Store.loader} />
    );
};

export default observer(App);
